import React, { useCallback } from 'react';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useColorPaletteProvider } from '../../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import type { EventScheduleFragment, FormattedEventScheduleFragment } from '@graphql/fragments/event.schedule.graphql.generated';
import type { Variant, VariantStyles } from '../ScheduleList.types';
import type { BoxProps } from '@withjoy/joykit';
import { Box, ButtonV2, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { useTranslations } from '../ScheduleList.i18n';
import { useScheduleListTelemetry } from '../ScheduleList.telemetry';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { button2ToButtonsOverride, hed4ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { ScheduleListItem } from './ScheduleListItem';

export interface ScheduleListContentProps
  extends Readonly<{
      eventId: string;
      formattedSchedule: ReadonlyArray<FormattedEventScheduleFragment>;
      tbaSchedule: ReadonlyArray<EventScheduleFragment>;
      eventHandle: string;
      variantStyles: Readonly<Record<Variant, VariantStyles>>;
      variant: Variant;
      layout?: string;
      shouldDisplayUnlockButton: Maybe<boolean>;
      handleUnlockDialogClick: () => void;
      unlockButtonText: string;
      scheduleListVersion: 'v1' | 'v2';
    }>,
    BoxProps {}

export const ScheduleListContent = React.forwardRef<HTMLDivElement, ScheduleListContentProps>((props, ref) => {
  const {
    children,
    eventHandle,
    shouldDisplayUnlockButton,
    handleUnlockDialogClick,
    unlockButtonText,
    formattedSchedule,
    tbaSchedule,
    layout,
    variant,
    eventId,
    variantStyles,
    scheduleListVersion,
    ...restProps
  } = props;
  const isMdOrSmScreen = useMediaQuery(theme => theme.mediaQueries.down({ breakpointAlias: 'sm3' }));
  const isStackVariant = variant === 'stack';
  const isHeaderInSecondaryBlock = isMdOrSmScreen || isStackVariant;
  const { pageFontColor: textColor } = useColorPaletteProvider();
  const borderBottomOverride = textColor ? { borderBottomColor: textColor } : {};

  const isScheduleListV1 = scheduleListVersion === 'v1';

  const styles = variantStyles[variant];
  const translations = useTranslations();
  const telemetry = useScheduleListTelemetry();

  const { formatDate } = useEventLocaleFormatter();

  const handleAddressClicked = useCallback(
    scheduleTitle => {
      telemetry.scheduleAddressClicked(scheduleTitle);
    },
    [telemetry],
  );

  const handleAddToCalendar = useCallback(
    (scheduleTitle: string, provider: string) => {
      telemetry.scheduleAddToCalendarClicked(scheduleTitle, provider);
    },
    [telemetry],
  );

  const handleVirtualEventClicked = useCallback(
    (scheduleTitle: string) => {
      telemetry.scheduleVirtualEventClicked(scheduleTitle);
    },
    [telemetry],
  );

  const _renderScheduleGroup = (key: string | number, header: string, items: ReadonlyArray<EventScheduleFragment>, index: number) => {
    if (!items.length) {
      return null;
    }
    const isToBeAnnounced = key === 'tba';
    const showTimeZone = items.reduce((resultSet, item) => resultSet.add(item.timezone), new Set()).size > 1;
    const headerMarginTop = shouldDisplayUnlockButton && index === 0 ? 7 : 0;

    return (
      <div
        key={key}
        data-testid={`schedule-group-${key}`}
      >
        <Box
          as="section"
          {...styles.container}
        >
          {isScheduleListV1 && (
            <Box
              as="header"
              {...styles.header}
              marginTop={headerMarginTop}
              {...borderBottomOverride}
            >
              <GuestSiteTypographyOverride override={hed4ToSubHeadingOverride}>
                <TextV2
                  data-testid={`groupheader-${key}`}
                  typographyVariant="hed4"
                  {...styles.headerText}
                >
                  {header}
                </TextV2>
              </GuestSiteTypographyOverride>
            </Box>
          )}
          <Flex
            flexDirection="column"
            role="list"
          >
            {items.map((item, ind) => {
              return (
                <ScheduleListItem
                  key={item.id}
                  scheduleListVersion={scheduleListVersion}
                  eventId={eventId}
                  eventHandle={eventHandle}
                  item={item}
                  isHeaderInSecondaryBlock={isScheduleListV1 ? isHeaderInSecondaryBlock : false}
                  isToBeAnnounced={isToBeAnnounced}
                  variantStyles={variantStyles}
                  variant={variant}
                  translations={translations}
                  showTimeZone={showTimeZone}
                  onAddressClicked={handleAddressClicked}
                  onCalendarClicked={handleAddToCalendar}
                  onVirtualEventBlockClicked={() => handleVirtualEventClicked(item.name)}
                  header={ind === 0 ? header : undefined}
                  groupKey={key}
                  groupIndex={index}
                />
              );
            })}
          </Flex>
        </Box>
      </div>
    );
  };

  return (
    <Box
      ref={ref}
      {...restProps}
      {...styles.events}
    >
      <>
        <Flex justifyContent={'center'}>
          {shouldDisplayUnlockButton && (
            <GuestSiteTypographyOverride override={button2ToButtonsOverride}>
              <ButtonV2
                onClick={handleUnlockDialogClick}
                variant="outline"
                shape="rounded"
              >
                {unlockButtonText}
              </ButtonV2>
            </GuestSiteTypographyOverride>
          )}
        </Flex>
        <SpacingStack {...styles.eventsStack}>
          {formattedSchedule.map(({ date: groupDate, items }, i) => {
            const groupDateHeader =
              formatDate(groupDate.timestamp, 'weekday\ndd MMMM yyyy', {
                titlecase: true,
                timezone: groupDate.timezone,
              }) ?? '';

            return _renderScheduleGroup(`${groupDate.milliseconds}-${i}`, groupDateHeader, items, i);
          })}
          {_renderScheduleGroup('tba', translations.toBeAnnounced, tbaSchedule, formattedSchedule.length)}
        </SpacingStack>
      </>
    </Box>
  );
});
